import { Localization, SessionContext } from "@viamap/viamap2-common";
import { useContext, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { BsQuestion } from "react-icons/bs";
import { ApplicationStateContext, Feature } from "src/states/ApplicationState";
import { TooltipWrapper } from "src/propertyInfoTemplates/PropertyComponents";
import { message } from "./Message";
import "./ServitutMolbakHelp.css";
import {
   canSubmitServitutHelp,
   MOLBAK_PRIVACY_POLICY_URL,
   readSessionContact,
   sendServitutHelpMail,
   type ServitutMolbakHelpContext,
} from "./servitutHelpRequest";

const EMPTY_CONTEXT: ServitutMolbakHelpContext = {
   matrikelNr: "",
   ejerlav: "",
   servitutNr: "",
};

export function ServitutMolbakHelpButton(props: { context?: ServitutMolbakHelpContext }) {
   const { hasAccessToFeature } = useContext(ApplicationStateContext);
   const [open, setOpen] = useState(false);
   if (!hasAccessToFeature(Feature.ServitutMolbakHelp)) {
      return null;
   }

   const hoverText = Localization.getText("Contact Mølbak Rådgivende Landinspektører A/S for help finding the easement or appendix");
   return (
      <>
         <TooltipWrapper title={hoverText} className="ViewButtonToolTip servitut-molbak-help__tooltip">
            <button
               type="button"
               className="servitut-molbak-help"
               aria-label={hoverText}
               onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  setOpen(true);
               }}
            >
               <BsQuestion />
            </button>
         </TooltipWrapper>
         {open ? (
            <ServitutMolbakHelpDialog
               context={props.context || EMPTY_CONTEXT}
               onClose={() => setOpen(false)}
            />
         ) : null}
      </>
   );
}

function ServitutMolbakHelpDialog(props: {
   context: ServitutMolbakHelpContext;
   onClose: () => void;
}) {
   const { state: sessionState } = useContext(SessionContext);
   const contact = readSessionContact(sessionState);
   const [matrikelNr, setMatrikelNr] = useState(props.context.matrikelNr);
   const [ejerlav, setEjerlav] = useState(props.context.ejerlav);
   const [servitutNr, setServitutNr] = useState(props.context.servitutNr);
   const [name, setName] = useState(contact.name);
   const [email, setEmail] = useState(contact.email);
   const [phone, setPhone] = useState(contact.phone);
   const [note, setNote] = useState("");
   const [consentPrivacy, setConsentPrivacy] = useState(false);
   const [sending, setSending] = useState(false);
   const canSend = canSubmitServitutHelp({ consentPrivacy, sending });

   useEffect(() => {
      function onKeyDown(event: KeyboardEvent) {
         if (event.key === "Escape" && !sending) {
            props.onClose();
         }
      }
      document.addEventListener("keydown", onKeyDown);
      return () => document.removeEventListener("keydown", onKeyDown);
   }, [props, sending]);

   async function submit() {
      if (!canSubmitServitutHelp({ consentPrivacy, sending })) {
         return;
      }
      setSending(true);
      try {
         await sendServitutHelpMail({
            matrikelNr,
            ejerlav,
            servitutNr,
            name,
            email,
            phone,
            message: note,
         }, {
            userRef: sessionState.userRef || "",
            userName: sessionState.userName || "",
            organizationId: sessionState.customerRef || "",
         });
         message.info(Localization.getText("The message was sent to tinglysning@molbak.dk."));
         props.onClose();
      } catch {
         message.error(Localization.getText("The message could not be sent."));
         setSending(false);
      }
   }

   return createPortal(
      <div
         className="servitut-molbak-help__overlay"
         role="presentation"
         onMouseDown={(event) => {
            if (event.target === event.currentTarget && !sending) {
               props.onClose();
            }
         }}
      >
         <form
            className="servitut-molbak-help__dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="servitut-molbak-help-title"
            onSubmit={(event) => {
               event.preventDefault();
               void submit();
            }}
         >
            <h2 id="servitut-molbak-help-title">{Localization.getText("Help with easements")}</h2>
            <p>{Localization.getText("Fill in the form and we will contact you as soon as possible.")}</p>
            <p>{Localization.getText("You are also welcome to contact us on +45 70 20 08 83.")}</p>
            <label>
               <span>{Localization.getText("Message")}</span>
               <textarea value={note} onChange={(event) => setNote(event.currentTarget.value)} rows={4} />
            </label>
            <label>
               <span>{Localization.getText("Cadastral no.")}</span>
               <input value={matrikelNr} onChange={(event) => setMatrikelNr(event.currentTarget.value)} />
            </label>
            <label>
               <span>{Localization.getText("Cadastral district")}</span>
               <input value={ejerlav} onChange={(event) => setEjerlav(event.currentTarget.value)} />
            </label>
            <label>
               <span>{Localization.getText("Easement no.")}</span>
               <input value={servitutNr} onChange={(event) => setServitutNr(event.currentTarget.value)} />
            </label>
            <label>
               <span>{Localization.getText("Name")}</span>
               <input value={name} onChange={(event) => setName(event.currentTarget.value)} autoComplete="name" />
            </label>
            <label>
               <span>{Localization.getText("Email")}</span>
               <input type="email" value={email} onChange={(event) => setEmail(event.currentTarget.value)} autoComplete="email" />
            </label>
            <label>
               <span>{Localization.getText("Phone")}</span>
               <input type="tel" value={phone} onChange={(event) => setPhone(event.currentTarget.value)} autoComplete="tel" />
            </label>
            <label className="servitut-molbak-help__check">
               <input
                  type="checkbox"
                  checked={consentPrivacy}
                  onChange={(event) => setConsentPrivacy(event.currentTarget.checked)}
               />
               <span>
                  {Localization.getText("I accept Mølbak's privacy policy")}{" "}
                  <a href={MOLBAK_PRIVACY_POLICY_URL} target="_blank" rel="noopener noreferrer">
                     {Localization.getText("Mølbak's privacy policy")}
                  </a>
               </span>
            </label>
            <div className="servitut-molbak-help__actions">
               <button type="button" className="servitut-molbak-help__close" onClick={props.onClose} disabled={sending}>
                  {Localization.getText("Close")}
               </button>
               <button type="submit" className="servitut-molbak-help__send" disabled={!canSend}>
                  {Localization.getText("Send")}
               </button>
            </div>
         </form>
      </div>,
      document.body,
   );
}
